import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axiosInstance';
import { useProject } from '../../context/ProjectContext';
import useWorkspaceRouting from '../../hooks/useWorkspaceRouting';
import { buildWorkspaceDashboardPath } from '../../utils/workspaceRouteUtils';
import { DraggableList } from '../common';
import LinkTestCasesModal from '../test-suites/LinkTestCasesModal';
import RunTestSuiteModal from '../test-suites/RunTestSuiteModal';
import EditTestSuiteModal from '../test-suites/EditTestSuiteModal';
import {
  Breadcrumbs,
  DataTable,
  ErrorState,
  LoadingState,
  MetricCard,
  PageScaffold,
  StatusBadge,
  Tabs,
} from '../ui';
import { formatDateTime } from '../../utils/dateTimeFormat';

/**
 * Standalone suite detail — cases table + reorder + actions.
 */
const SuiteDetailPage = () => {
  const { suiteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const { getWorkspaceIdFromURL, getProjectIdFromURL } = useWorkspaceRouting();

  const projectId =
    searchParams.get('project_id') || getProjectIdFromURL() || selectedProject?.id;
  const projectName = selectedProject?.name || 'Project';

  const [suite, setSuite] = useState(null);
  const [cases, setCases] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [tab, setTab] = useState('cases');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [linkOpen, setLinkOpen] = useState(false);
  const [runOpen, setRunOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [reordering, setReordering] = useState(false);

  const goBack = useCallback(() => {
    const workspaceId = getWorkspaceIdFromURL();
    const ret = searchParams.get('ret');
    let listQuery = '';
    if (ret) {
      try {
        listQuery = `?${decodeURIComponent(ret)}`;
      } catch {
        listQuery = `?${ret}`;
      }
    } else if (projectId) {
      const saved = sessionStorage.getItem(`rt-suites-list:${projectId}`);
      if (saved) listQuery = saved.startsWith('?') ? saved : `?${saved}`;
    }
    if (workspaceId) {
      navigate(`${buildWorkspaceDashboardPath(workspaceId, projectId, 'test-suites')}${listQuery}`);
    } else {
      navigate(`/dashboard/test-suites${listQuery}`);
    }
  }, [navigate, getWorkspaceIdFromURL, projectId, searchParams]);

  const load = useCallback(async () => {
    if (!projectId || !suiteId) {
      setError('Missing suite or project context.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const listRes = await axiosInstance.get(`/test_suite/list/${projectId}`, {
        params: { page: 1, page_size: 100, sort_by: 1, sort_order: 2 },
      });
      const list = listRes?.test_suites || [];
      const found = (Array.isArray(list) ? list : []).find(
        (s) => String(s.suite_id || s.id) === String(suiteId)
      );
      setSuite(found || { suite_id: suiteId, suite_name: `Suite ${suiteId}` });

      const casesRes = await axiosInstance.get('/test_case/suite_cases', {
        params: { suite_id: suiteId, page: 1, page_size: 200 },
      });
      const caseRows =
        casesRes?.test_cases ||
        casesRes?.data ||
        (Array.isArray(casesRes) ? casesRes : []);
      setCases(Array.isArray(caseRows) ? caseRows : []);

      try {
        const execRes = await axiosInstance.get('/test_case/get_test_suite_execution', {
          params: { suite_id: suiteId },
        });
        const execRows =
          execRes?.data || execRes?.executions || (Array.isArray(execRes) ? execRes : []);
        setExecutions(Array.isArray(execRows) ? execRows : []);
      } catch {
        setExecutions([]);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Failed to load suite');
    } finally {
      setLoading(false);
    }
  }, [projectId, suiteId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCase = (row) => {
    const id = row.test_case_id || row.id;
    const workspaceId = getWorkspaceIdFromURL();
    const suiteName = encodeURIComponent(
      suite?.suite_name || suite?.name || `Suite ${suiteId}`
    );
    const qs = `project_id=${projectId}&from=suite&suite_id=${suiteId}&suite_name=${suiteName}`;
    const path = `test-cases/${id}?${qs}`;
    if (workspaceId) {
      navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
    } else {
      navigate(`/dashboard/${path}`);
    }
  };

  const startReorder = () => {
    setReorderItems(cases.map((c) => ({ ...c, test_case_id: c.test_case_id || c.id })));
    setReorderMode(true);
  };

  const cancelReorder = () => {
    setReorderMode(false);
    setReorderItems([]);
  };

  const handleCaseOrderChange = async (nextItems) => {
    const previous = reorderItems;
    setReorderItems(nextItems);
    setReordering(true);
    try {
      const response = await axiosInstance.put('/test_case/reorder_cases', {
        project_id: projectId,
        suite_id: Number(suiteId) || suiteId,
        order: nextItems.map((tc) => tc.test_case_id || tc.id),
      });
      if (response?.status === 'success' || response?.status_code === 200) {
        setCases(nextItems);
        setReorderMode(false);
        setReorderItems([]);
        toast.success(response?.message || 'Test cases reordered');
      } else {
        throw new Error(response?.message || 'Failed to reorder test cases');
      }
    } catch (err) {
      setReorderItems(previous);
      toast.error(err?.response?.data?.message || err?.message || 'Failed to reorder');
    } finally {
      setReordering(false);
    }
  };

  const caseColumns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Test Case',
        render: (row) => row.name || row.test_case_name || `#${row.test_case_id || row.id}`,
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
        key: 'priority',
        header: 'Priority',
        render: (row) => row.priority || '—',
      },
      {
        key: 'automation',
        header: 'Automation',
        render: (row) => row.automation_status || '—',
      },
    ],
    []
  );

  const execColumns = useMemo(
    () => [
      {
        key: 'id',
        header: 'Execution',
        render: (row) =>
          `#${row.suite_execution_id || row.execution_id || row.id}`,
      },
      {
        key: 'status',
        header: 'Status',
        render: (row) => <StatusBadge status={row.status} />,
      },
      {
        key: 'started',
        header: 'Started',
        render: (row) => {
          const d = row.started_at || row.created_at;
          return formatDateTime(d, { fallback: '—' });
        },
      },
      {
        key: 'duration',
        header: 'Duration',
        render: (row) =>
          row.duration || row.execution_time_sec
            ? `${row.duration || row.execution_time_sec}s`
            : '—',
      },
    ],
    []
  );

  if (loading) {
    return (
      <PageScaffold title="Suite" breadcrumbs={<Breadcrumbs items={[{ label: 'Test Suites', path: 'x' }, { label: '…' }]} onNavigate={goBack} />}>
        <LoadingState />
      </PageScaffold>
    );
  }

  if (error && !suite) {
    return (
      <PageScaffold title="Suite">
        <ErrorState description={error} onRetry={load} />
      </PageScaffold>
    );
  }

  return (
    <PageScaffold
      title={suite?.suite_name || suite?.name || `Suite ${suiteId}`}
      breadcrumbs={
        <Breadcrumbs
          items={[
            { label: 'Test Suites', path: 'test-suites' },
            { label: suite?.suite_name || `#${suiteId}` },
          ]}
          onNavigate={goBack}
        />
      }
      compact
      actions={
        <>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={goBack}>
            ← Suites
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={() => setEditOpen(true)}>
            Edit
          </button>
          <button type="button" className="rt-btn rt-btn--secondary" onClick={() => setLinkOpen(true)}>
            Link cases
          </button>
          <button type="button" className="rt-btn rt-btn--primary" onClick={() => setRunOpen(true)}>
            Run Suite
          </button>
        </>
      }
    >
      <div className="rt-metric-grid">
        <MetricCard label="Cases" tone="violet" value={cases.length} />
        <MetricCard label="Executions" tone="primary" value={executions.length} />
        <MetricCard label="Status" tone="success" value={suite?.status || 'active'} />
        <MetricCard
          label="Mode"
          tone="info"
          value={
            suite?.execution_mode === 1 || suite?.execution_mode === '1'
              ? 'Parallel'
              : suite?.execution_mode === 2 || suite?.execution_mode === '2'
                ? 'Sequence'
                : suite?.execution_mode || '—'
          }
        />
      </div>

      <Tabs
        activeId={tab}
        onChange={(id) => {
          if (reorderMode) cancelReorder();
          setTab(id);
        }}
        tabs={[
          { id: 'cases', label: 'Cases', count: cases.length },
          { id: 'history', label: 'History', count: executions.length },
        ]}
      />

      <div style={{ marginTop: 12 }}>
        {tab === 'cases' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 10 }}>
              {reorderMode ? (
                <>
                  <span className="rt2-muted" style={{ alignSelf: 'center', fontSize: 13 }}>
                    Drag rows to reorder · saves on drop
                    {reordering ? ' · saving…' : ''}
                  </span>
                  <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={cancelReorder}>
                    Done
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="rt-btn rt-btn--secondary rt-btn--sm"
                  onClick={startReorder}
                  disabled={!cases.length}
                >
                  Reorder Test Cases
                </button>
              )}
            </div>
            {reorderMode ? (
              <div className="rt-reorder-list">
                <DraggableList
                  items={reorderItems}
                  itemKey="test_case_id"
                  onReorder={handleCaseOrderChange}
                  renderItem={(tc) => (
                    <div className="rt-reorder-row">
                      <span className="rt-reorder-handle" aria-hidden>
                        ⋮⋮
                      </span>
                      <div className="rt-reorder-row__body">
                        <div className="rt-reorder-row__title">
                          {tc.name || tc.test_case_name || `#${tc.test_case_id}`}
                        </div>
                        <div className="rt-reorder-row__meta">
                          <StatusBadge status={tc.status} />
                          {tc.priority ? <span>{tc.priority}</span> : null}
                        </div>
                      </div>
                    </div>
                  )}
                />
              </div>
            ) : (
              <DataTable
                columns={caseColumns}
                rows={cases}
                rowKey="test_case_id"
                onRowClick={openCase}
                emptyTitle="No cases linked"
                emptyDescription="Link test cases to this suite."
              />
            )}
          </>
        )}
        {tab === 'history' && (
          <DataTable
            columns={execColumns}
            rows={executions}
            rowKey="suite_execution_id"
            emptyTitle="No suite executions yet"
            onRowClick={(row) => {
              const id = row.suite_execution_id || row.execution_id || row.id;
              const workspaceId = getWorkspaceIdFromURL();
              const path = `executions/${id}?type=suite&entity_id=${suiteId}&name=${encodeURIComponent(suite?.suite_name || '')}`;
              if (workspaceId) {
                navigate(buildWorkspaceDashboardPath(workspaceId, projectId, path));
              }
            }}
          />
        )}
      </div>

      <LinkTestCasesModal
        isOpen={linkOpen}
        onClose={() => setLinkOpen(false)}
        testSuite={suite}
        projectId={projectId}
        mode="link"
        onSuccess={() => {
          setLinkOpen(false);
          load();
        }}
      />
      <RunTestSuiteModal
        isOpen={runOpen}
        onClose={() => setRunOpen(false)}
        testSuiteId={suiteId}
        testSuiteName={suite?.suite_name || suite?.name}
        projectId={projectId}
        environmentId={suite?.environment_id}
        onSuccess={() => {
          setRunOpen(false);
          load();
        }}
      />
      {editOpen && (
        <EditTestSuiteModal
          isOpen={editOpen}
          onClose={() => setEditOpen(false)}
          testSuite={suite}
          projects={[{ project_id: projectId, project_name: projectName }]}
          onSaveSuccess={() => {
            setEditOpen(false);
            load();
          }}
        />
      )}
    </PageScaffold>
  );
};

export default SuiteDetailPage;
